import {
  ArrowLeftIcon,
  CalculatorIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CurrencyDollarIcon,
  DocumentPlusIcon,
  MapPinIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import AISalesAssistant from '../../components/ai/AISalesAssistant';
import DealCalculatorModal from '../../components/deals/DealCalculatorModal';
import DocumentGenerator from '../../components/documents/DocumentGenerator';
import { AddExpenseModal } from '../../components/inventory-costs/AddExpenseModal';
import VehicleImageUpload from '../../components/vehicles/VehicleImageUpload';
import VehicleModal from '../../components/vehicles/VehicleModal';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { Vehicle } from '../../types';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { canViewCosts } from '../../utils/roles';

interface VehicleImage {
  id: string;
  file_name: string;
  file_path: string;
  is_primary: boolean;
  display_order: number;
  isExternalUrl?: boolean; // Flag for imported external URLs (e.g., CarsForSale CDN)
}

const VehicleDetailPage: React.FC = () => {
  const { id, vin } = useParams<{ id?: string; vin?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const showCosts = canViewCosts(user?.role);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);

  // Action loading states
  const [actionLoading, setActionLoading] = useState(false);

  // Determine which parameter we're using
  const identifier = vin || id;
  const isVinRoute = !!vin;

  const fetchVehicleImages = useCallback(async () => {
    if (!identifier) return;
    try {
      // First try to get uploaded images from vehicle_images table
      const endpoint = isVinRoute
        ? `/vehicles/vin/${identifier}/vehicle-images`
        : `/vehicles/${identifier}/vehicle-images`;
      const uploadedImages = await apiService.get<VehicleImage[]>(endpoint);

      if (uploadedImages && uploadedImages.length > 0) {
        setImages(uploadedImages);
        setSelectedImageIndex(0);
        return;
      }

      // Fallback: Get images from vehicle's images JSON field (imported from CSV/CarsForSale)
      const vehicleEndpoint = isVinRoute
        ? `/vehicles/vin/${identifier}`
        : `/vehicles/${identifier}`;
      const vehicleData = await apiService.get<{ images?: string[] }>(vehicleEndpoint);

      if (vehicleData?.images && vehicleData.images.length > 0) {
        // Convert external URLs to VehicleImage format for consistent rendering
        const externalImages: VehicleImage[] = vehicleData.images.map((url, index) => ({
          id: `external-${index}`,
          file_name: url.split('/').pop() || `image-${index}`,
          file_path: url, // Full external URL
          is_primary: index === 0,
          display_order: index,
          isExternalUrl: true,
        }));
        setImages(externalImages);
        setSelectedImageIndex(0);
        return;
      }

      // No images found
      setImages([]);
    } catch (error) {
      console.error('Error fetching vehicle images:', error);
      setImages([]);
    }
  }, [identifier, isVinRoute]);

  const fetchVehicle = useCallback(async () => {
    if (!identifier) return;
    try {
      setLoading(true);
      setError(null);
      const endpoint = isVinRoute ? `/vehicles/vin/${identifier}` : `/vehicles/${identifier}`;
      const [vehicleResponse] = await Promise.all([
        apiService.get<Vehicle>(endpoint),
        fetchVehicleImages(),
      ]);
      setVehicle(vehicleResponse);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setError('Failed to load vehicle details');
    } finally {
      setLoading(false);
    }
  }, [identifier, isVinRoute, fetchVehicleImages]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showImageUpload) {
        setShowImageUpload(false);
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (showImageUpload) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showImageUpload]);

  useEffect(() => {
    fetchVehicle();
  }, [fetchVehicle]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (number: number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      available: 'bg-green-100 text-green-800',
      sold: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      wholesale: 'bg-orange-100 text-orange-800',
      repo: 'bg-red-100 text-red-800',
      service: 'bg-gray-100 text-gray-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  // Action handlers
  const handleEditVehicle = () => {
    setShowEditModal(true);
  };

  const handleSaveVehicle = async (vehicleData: any) => {
    if (!vehicle) return;
    try {
      setActionLoading(true);

      // Filter out fields that are not accepted by the backend VehicleUpdate model
      // VIN should not be updated as it's an immutable identifier
      const { vin: _vin, ...filteredData } = vehicleData;

      // Remove undefined/null values to prevent validation errors
      const updateData: any = {};
      // BHPH numeric fields must be sent even when cleared (as null) so clearing a
      // previously-set value persists (backend uses exclude_unset).
      const BHPH_NULLABLE_FIELDS = [
        'bhph_min_down_value',
        'bhph_payment_amount',
        'bhph_term_months',
      ];
      Object.keys(filteredData).forEach((key) => {
        const value = filteredData[key];
        if (BHPH_NULLABLE_FIELDS.includes(key)) {
          updateData[key] =
            value === '' || value === undefined || value === null ? null : Number(value);
          return;
        }
        if (value !== undefined && value !== null) {
          updateData[key] = value;
        }
      });

      const updatedVehicle = await apiService.put<Vehicle>(`/vehicles/${vehicle.id}`, updateData);
      setVehicle(updatedVehicle);
      setShowEditModal(false);
      toast.success('Vehicle updated successfully');
      return updatedVehicle;
    } catch (error) {
      console.error('Error updating vehicle:', error);
      toast.error('Failed to update vehicle');
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateDeal = () => {
    if (!vehicle) return;
    // Navigate to deal calculator with vehicle pre-selected
    navigate('/calculator', {
      state: {
        selectedVehicle: {
          id: vehicle.id,
          vin: vehicle.vin,
          year: vehicle.year,
          make: vehicle.make,
          model: vehicle.model,
          askingPrice: vehicle.asking_price,
        },
      },
    });
  };

  const handleAddPhotos = () => {
    setShowImageUpload(true);
  };

  const handleImagesChange = (newImages: VehicleImage[]) => {
    setImages(newImages);

    // Primary image is now first in the array from backend
    if (newImages && newImages.length > 0) {
      setSelectedImageIndex(0);
    }
  };

  const handleUpdatePrice = () => {
    setShowPriceModal(true);
  };

  const handleMarkAsSold = () => {
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!vehicle) return;
    try {
      setActionLoading(true);
      const updatedVehicle = await apiService.patch<Vehicle>(`/vehicles/${vehicle.id}`, {
        status: newStatus,
      });
      setVehicle(updatedVehicle);
      setShowStatusModal(false);
      toast.success(`Vehicle marked as ${newStatus.toLowerCase()}`);
    } catch (error) {
      console.error('Error updating vehicle status:', error);
      toast.error('Failed to update vehicle status');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePriceUpdate = async (prices: {
    asking_price?: number;
    purchase_price?: number;
    market_value?: number;
  }) => {
    if (!vehicle) return;
    try {
      setActionLoading(true);
      const updatedVehicle = await apiService.patch<Vehicle>(`/vehicles/${vehicle.id}`, prices);
      setVehicle(updatedVehicle);
      setShowPriceModal(false);
      toast.success('Pricing updated successfully');
    } catch (error) {
      console.error('Error updating vehicle pricing:', error);
      toast.error('Failed to update pricing');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Vehicles
          </button>
        </div>
        <div className="card animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vehicle) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Vehicles
          </button>
        </div>
        <div className="card">
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TruckIcon className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {typeof error === 'string'
                ? error
                : (error as any)?.message || (error as any)?.detail || 'Vehicle not found'}
            </h3>
            <p className="text-gray-500">
              The vehicle you're looking for doesn't exist or couldn't be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${showImageUpload ? 'pointer-events-none' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate('/vehicles')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Vehicles
        </button>
        <button className="btn-primary" onClick={handleEditVehicle} disabled={actionLoading}>
          <PencilIcon className="h-4 w-4 mr-2" />
          Edit Vehicle
        </button>
      </div>

      {/* Vehicle Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Info */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TruckIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                      {vehicle.trim && ` ${vehicle.trim}`}
                    </h1>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(vehicle.status)}`}
                    >
                      {vehicle.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Image Gallery - Carousel Layout */}
            <div className="mb-6">
              {images.length > 0 ? (
                <div className="space-y-4">
                  {/* Main Carousel Stage */}
                  <div className="relative h-[400px] bg-gray-100 rounded-lg overflow-hidden group">
                    <img
                      src={
                        images[selectedImageIndex]?.isExternalUrl
                          ? images[selectedImageIndex]?.file_path
                          : apiService.getFileUrl(images[selectedImageIndex]?.file_path || '')
                      }
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - View ${selectedImageIndex + 1}`}
                      className="w-full h-full object-contain bg-gray-900"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src =
                          'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="400" height="300" fill="%23f3f4f6"/><text x="200" y="150" text-anchor="middle" fill="%236b7280" font-size="16">Image not available</text></svg>';
                      }}
                    />

                    {/* Navigation Arrows */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) =>
                          prev === 0 ? images.length - 1 : prev - 1
                        );
                      }}
                      aria-label="Previous image"
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageIndex((prev) =>
                          prev === images.length - 1 ? 0 : prev + 1
                        );
                      }}
                      aria-label="Next image"
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRightIcon className="h-6 w-6" />
                    </button>

                    {/* Image Counter Badge */}
                    <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {selectedImageIndex + 1} / {images.length}
                    </div>

                    {/* Primary Badge */}
                    {images[selectedImageIndex]?.is_primary && (
                      <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm">
                        Primary
                      </div>
                    )}
                  </div>

                  {/* Thumbnails Scroll Strip */}
                  <div className="relative p-2 bg-gray-50 rounded-lg border border-gray-100">
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent px-1">
                      {images.map((image, index) => (
                        <button
                          key={image.id}
                          onClick={() => setSelectedImageIndex(index)}
                          className={`relative flex-shrink-0 w-24 h-24 rounded-md overflow-hidden transition-all ${
                            selectedImageIndex === index
                              ? 'ring-2 ring-blue-500 ring-offset-2 opacity-100'
                              : 'opacity-70 hover:opacity-100 hover:ring-1 hover:ring-gray-300'
                          }`}
                        >
                          <img
                            src={
                              image.isExternalUrl
                                ? image.file_path
                                : apiService.getFileUrl(image.file_path)
                            }
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-80 bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-3" />
                    <p className="text-lg font-medium text-gray-500 mb-2">No photos available</p>
                    <button className="btn-primary" onClick={handleAddPhotos}>
                      <PhotoIcon className="h-4 w-4 mr-2" />
                      Add Photos
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Vehicle Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Vehicle Details</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">VIN</p>
                    <p className="text-sm text-gray-600 font-mono">{vehicle.vin}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Mileage</p>
                    <p className="text-sm text-gray-600">
                      {vehicle.mileage ? formatNumber(vehicle.mileage) : 'N/A'} miles
                    </p>
                  </div>
                  {vehicle.exterior_color && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Exterior Color</p>
                      <p className="text-sm text-gray-600">{vehicle.exterior_color}</p>
                    </div>
                  )}
                  {vehicle.interior_color && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Interior Color</p>
                      <p className="text-sm text-gray-600">{vehicle.interior_color}</p>
                    </div>
                  )}
                  {vehicle.body_style && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Body Style</p>
                      <p className="text-sm text-gray-600">{vehicle.body_style}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Mechanical Details */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Mechanical Details</h3>
                <div className="space-y-3">
                  {vehicle.engine && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Engine</p>
                      <p className="text-sm text-gray-600">{vehicle.engine}</p>
                    </div>
                  )}
                  {vehicle.transmission && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Transmission</p>
                      <p className="text-sm text-gray-600">{vehicle.transmission}</p>
                    </div>
                  )}
                  {vehicle.fuel_type && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Fuel Type</p>
                      <p className="text-sm text-gray-600">{vehicle.fuel_type}</p>
                    </div>
                  )}
                  {vehicle.location && (
                    <div className="flex items-center">
                      <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Location</p>
                        <p className="text-sm text-gray-600">{vehicle.location}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing - visible to managers and above only */}
            {showCosts && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Asking Price</p>
                    <p className="text-xl font-bold text-blue-900">
                      {formatCurrency(vehicle.asking_price)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Acquisition Cost</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(vehicle.acquisition_cost)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">Total Cost</p>
                    <p className="text-lg font-bold text-green-900">
                      {formatCurrency(vehicle.total_cost)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => setExpenseModalOpen(true)}
                    className="btn-secondary text-sm inline-flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" /> Add Expense
                  </button>
                </div>
              </div>
            )}

            {/* Description */}
            {vehicle.description && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
                <div
                  className="text-sm text-gray-600 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(vehicle.description) }}
                />
              </div>
            )}

            {/* Features */}
            {vehicle.features && vehicle.features.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Features</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {vehicle.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Activity Timeline */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
            </div>
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium text-gray-900">Vehicle Added</p>
                <p className="text-gray-600">{formatDate(vehicle.created_at)}</p>
              </div>
              {vehicle.updated_at && vehicle.updated_at !== vehicle.created_at && (
                <div className="text-sm">
                  <p className="font-medium text-gray-900">Last Updated</p>
                  <p className="text-gray-600">{formatDate(vehicle.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            </div>
            <div className="space-y-2">
              <button
                className="w-full btn-secondary text-left flex items-center"
                onClick={handleCreateDeal}
                disabled={actionLoading}
              >
                <DocumentPlusIcon className="h-4 w-4 mr-2" />
                Create Deal
              </button>
              <button
                className="w-full btn-secondary text-left flex items-center"
                onClick={handleAddPhotos}
                disabled={actionLoading}
              >
                <PhotoIcon className="h-4 w-4 mr-2" />
                Add Photos
              </button>
              <button
                className="w-full btn-primary text-left flex items-center"
                onClick={() => setShowCalculator(true)}
              >
                <CalculatorIcon className="h-4 w-4 mr-2" />
                Calculate Deal
              </button>
              <button
                className="w-full btn-secondary text-left flex items-center"
                onClick={handleUpdatePrice}
                disabled={actionLoading}
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                Update Price
              </button>
              <button
                className="w-full btn-secondary text-left flex items-center"
                onClick={handleMarkAsSold}
                disabled={actionLoading || vehicle?.status === 'sold'}
              >
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {vehicle?.status === 'sold' ? 'Already Sold' : 'Mark as Sold'}
              </button>
            </div>
          </div>

          {/* Vehicle Stats */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Vehicle Stats</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Days on Lot</span>
                <span className="font-medium">
                  {Math.floor(
                    (new Date().getTime() - new Date(vehicle.created_at).getTime()) /
                      (1000 * 60 * 60 * 24)
                  )}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Views</span>
                <span className="font-medium">--</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Inquiries</span>
                <span className="font-medium">--</span>
              </div>
            </div>
          </div>

          {/* AI Sales Assistant */}
          <AISalesAssistant vehicleId={vehicle.id} vehiclePrice={vehicle.asking_price} />

          {/* Document Generator */}
          <DocumentGenerator vehicleId={vehicle.id} compact />
        </div>
      </div>

      {/* Modals */}
      {/* Edit Vehicle Modal */}
      <VehicleModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleSaveVehicle}
        vehicle={vehicle}
        loading={actionLoading}
      />

      {/* Image Upload Modal */}
      {showImageUpload && vehicle && (
        <div
          className="fixed inset-0 z-[60] overflow-y-auto pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowImageUpload(false)}
            />

            <div
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute right-0 top-0 pr-4 pt-4">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  onClick={() => setShowImageUpload(false)}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    Manage Vehicle Images
                  </h3>

                  <VehicleImageUpload
                    vehicleId={vehicle.id}
                    images={images}
                    onImagesChange={handleImagesChange}
                    onCloseModal={() => setShowImageUpload(false)}
                  />

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => setShowImageUpload(false)}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Price Update Modal */}
      {showPriceModal && vehicle && (
        <PriceUpdateModal
          isOpen={showPriceModal}
          onClose={() => setShowPriceModal(false)}
          onSave={handlePriceUpdate}
          currentPrices={{
            asking_price: vehicle.asking_price,
            acquisition_cost: vehicle.acquisition_cost,
            total_cost: vehicle.total_cost,
          }}
          loading={actionLoading}
        />
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <StatusUpdateModal
          isOpen={showStatusModal}
          onClose={() => setShowStatusModal(false)}
          onConfirm={() => handleStatusUpdate('SOLD')}
          loading={actionLoading}
        />
      )}

      {/* Deal Calculator Modal */}
      <DealCalculatorModal
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
        vehiclePrice={vehicle?.asking_price}
        vehicleInfo={{
          year: vehicle?.year,
          make: vehicle?.make,
          model: vehicle?.model,
        }}
      />

      {/* Add Expense Modal */}
      {vehicle && (
        <AddExpenseModal
          open={expenseModalOpen}
          onClose={() => setExpenseModalOpen(false)}
          vehicle={{
            id: vehicle.id,
            label: `${vehicle.year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim(),
          }}
          onSuccess={() => fetchVehicle()}
        />
      )}
    </div>
  );
};

// Price Update Modal Component
interface PriceUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prices: {
    asking_price?: number;
    acquisition_cost?: number;
    total_cost?: number;
  }) => void;
  currentPrices: {
    asking_price?: number;
    acquisition_cost?: number;
    total_cost?: number;
  };
  loading: boolean;
}

const PriceUpdateModal: React.FC<PriceUpdateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentPrices,
  loading,
}) => {
  const [askingPrice, setAskingPrice] = useState(currentPrices.asking_price?.toString() || '');
  const [acquisitionCost, setAcquisitionCost] = useState(
    currentPrices.acquisition_cost?.toString() || ''
  );
  const [totalCost, setTotalCost] = useState(currentPrices.total_cost?.toString() || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const prices: any = {};
    if (askingPrice) prices.asking_price = parseFloat(askingPrice);
    if (acquisitionCost) prices.acquisition_cost = parseFloat(acquisitionCost);
    if (totalCost) prices.total_cost = parseFloat(totalCost);
    onSave(prices);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">Update Pricing</h3>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Asking Price</label>
                  <input
                    type="number"
                    className="form-input"
                    value={askingPrice}
                    onChange={(e) => setAskingPrice(e.target.value)}
                    placeholder="25000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="form-label">Acquisition Cost</label>
                  <input
                    type="number"
                    className="form-input"
                    value={acquisitionCost}
                    onChange={(e) => setAcquisitionCost(e.target.value)}
                    placeholder="20000"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="form-label">Total Cost</label>
                  <input
                    type="number"
                    className="form-input"
                    value={totalCost}
                    onChange={(e) => setTotalCost(e.target.value)}
                    placeholder="22000"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Updating...' : 'Update Prices'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Status Update Modal Component
interface StatusUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">Mark as Sold</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to mark this vehicle as sold? This action will update the
                  vehicle status.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="btn-primary w-full sm:ml-3 sm:w-auto"
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Mark as Sold'}
            </button>
            <button
              type="button"
              className="btn-secondary mt-3 w-full sm:mt-0 sm:w-auto"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailPage;
